import { WrapChildrenWithText } from '@eigtech/ui/components/typopgraphy'
import { FieldArrayPath, FieldValues } from 'react-hook-form'
import { ArrayFieldAddButton } from './ArrayFieldButtons'
import { useArrayFieldContext } from './ArrayFieldContext'
import { ArrayFieldRow } from './ArrayFieldRow'
import { ArrayFieldRowContext, useArrayFieldRowContext } from './ArrayFieldRowContext'

export type ArrayFieldRowsProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
> = {
  emptyMessage?: React.ReactNode
  RowsContainer?: (props: { children: React.ReactNode }) => React.JSX.Element
  RowContainer?: (props: { children: React.ReactNode; index: number }) => React.JSX.Element
  renderRow: (
    field: ArrayFieldRowContext<TFieldValues, TFieldArrayName, TKeyName>,
    index: number
  ) => React.ReactNode
}

export function ArrayFieldRows<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>({
  emptyMessage,
  RowsContainer = DefaultRowsContainer,
  RowContainer,
  renderRow,
}: ArrayFieldRowsProps<TFieldValues, TFieldArrayName, TKeyName>) {
  const { fields } = useArrayFieldContext<TFieldValues>()

  return (
    <RowsContainer>
      {!fields.length ? (
        <WrapChildrenWithText>
          {emptyMessage ?? 'Use the "+" button to add a row'}
        </WrapChildrenWithText>
      ) : (
        fields.map((field, index) => (
          <ArrayFieldRow key={field.id} RowContainer={RowContainer} field={field} index={index}>
            <RenderRow index={index} renderRow={renderRow} />
          </ArrayFieldRow>
        ))
      )}
    </RowsContainer>
  )
}

function DefaultRowsContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-4">{children}</div>

      <div className="flex flex-row items-center justify-end">
        <ArrayFieldAddButton />
      </div>
    </div>
  )
}

function RenderRow<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>({
  renderRow,
  index,
}: ArrayFieldRowsProps<TFieldValues, TFieldArrayName, TKeyName> & {
  index: number
}) {
  const context = useArrayFieldRowContext<TFieldValues, TFieldArrayName, TKeyName>()

  return <>{renderRow(context, index)}</>
}

export type ArrayFieldRowsComponent<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
> = typeof ArrayFieldRows<TFieldValues, TFieldArrayName, TKeyName>
